import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import store from '@Store';
import isUndefined from 'lodash/isUndefined';
import pick from 'lodash/pick';
import filter from 'lodash/filter';
import some from 'lodash/some';
import fromPairs from 'lodash/fromPairs';
import find from 'lodash/find';
import get from 'lodash/get';
import FactsContext from '@Components/Results/substances/contexts/facts.context';
import { SearchTypes } from '@Constants/CommonConstants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import StructureModel from '@Components/Results/structure/structure-model.service';
import StructureZoomService from '@Services/StructureZoom';
import SubstanceIdentificationInformation from '@Components/Results/substances/information/SubstanceIdentificationInformation';
import SubstanceMoreNamesInformation from '@Components/Results/substances/information/SubstanceMoreNamesInformation';
import SubstanceSupplierInformation from '@Components/Results/substances/information/SubstanceSupplierInformation';
import Structure from '@Components/Results/structure/Structure';

import SubstanceHelper from '@Components/Results/services/substance-helper.factory';
import SubstanceDetailsContainer from '@Components/Results/substances/details/SubstanceDetailsContainer';
import SubstanceHitData from '@Components/Results/substances/information/SubstanceHitData';
import SubstanceInformation from '@Components/Results/substances/information/SubstanceInformation';
import { contextFromMetadata } from '@Components/Results/utils/metadata';
import { getExpandTopSubstanceFactMappingValue, getUIDirectiveValue, UI_DIRECTIVES } from '@Utils/UIDirectivesParameterHelper';
import Catalyst from '@Services/Catalyst';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';

import './Substance.scss';

function prepareFacts(facts, isMarkushSearch) {
  return (
    facts &&
    fromPairs(
      facts.map((fact) => {
        if (isMarkushSearch && fact.id === 'ID') {
          const markushFact = find(fact.children, { type: 'markush' });
          if (markushFact) {
            markushFact.open = true;
          }
        }
        return [
          fact.id,
          {
            ...fact,
            open: fact.hitData || (isMarkushSearch && fact.id === 'ID'),
          },
        ];
      })
    )
  );
}

function computeNewState(originalState, header, fact, value) {
  if (header && originalState) return false;
  if (fact?.id) return true;
  return !isUndefined(value) ? value : !originalState;
}

function Substance({ metadata, substance, zoom, searchType, gridView = false, size }) {
  const isMarkushSearch = searchType === SearchTypes.MARKUSH_SEARCH;
  const initialFacts = prepareFacts(substance.facts, isMarkushSearch);

  const [isFactsDataOpen, setIsFactsDataOpen] = useState(isMarkushSearch);
  const [alternativeContent, setAlternativeContent] = useState({});
  const [facts, setFacts] = useState(initialFacts);

  const zoomClasses = StructureZoomService.getZoomClasses('substances') || [];
  const database = SubstanceHelper.getCurrentDatabase(metadata.databaseId || substance.databaseId);
  const structureModel = React.useMemo(() => new StructureModel(substance), [substance]);

  const toggleFact = useCallback(
    (id, open, shouldScroll) => {
      if (!facts) return Promise.resolve(null);

      const originalState = facts[id]?.open;
      const updatedFacts = {
        ...facts,
        [id]: {
          ...facts[id],
          open: isUndefined(open) ? !originalState : open,
        },
      };

      const currentFact = updatedFacts[id];
      if (!currentFact) {
        Catalyst.trackError({ message: `Can't find fact by the ID, the data is corrupted.`, component: 'Substance' }, 'se-');
        return Promise.resolve(null);
      }

      return SubstanceHelper.checkAndRetrieveFactData(currentFact, structureModel, metadata)
        .then(() => {
          const isOpen = currentFact.open;
          if (originalState !== isOpen) {
            setFacts(updatedFacts);
          }
          if (isOpen) {
            Catalyst.trackAmountOfViewedData(
              get(structureModel, 'details.id', ''),
              currentFact,
              currentFact.type,
              currentFact.title,
              store.getState()
            );
          }
          if (shouldScroll) {
            const targetSelector = `#substances-${currentFact.hitData ? 'hitdata' : 'alldata'}-${structureModel.index} .rx-accordion-header.${currentFact.className}`;
            document.querySelector(targetSelector)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return isOpen;
        })
        .catch(Logger.reject);
    },
    [facts, metadata, structureModel]
  );

  const onToggleAlternativeInfo = useCallback((id) => {
    setAlternativeContent((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const toggleState = useCallback(
    (flag, value, fact, header) => {
      const originalState = { ...pick({ isFactsDataOpen }, flag) }[flag];
      const newState = computeNewState(originalState, header, fact, value);

      if (originalState !== newState) {
        setIsFactsDataOpen(newState);
      }

      if (!fact) return;

      if (!fact.open || !isFactsDataOpen) {
        const trackingLabel = metadata.isViewPreview ? `query:preview:${contextFromMetadata(metadata)}` : undefined;
        Catalyst.trackContentLinkClick(fact.title, trackingLabel);
      }

      const id = `accordion-${fact.className}-${structureModel.index}`;
      if (!originalState && fact.open) {
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      } else {
        toggleFact(fact.id, value)
          .then((open) => open)
          .catch(Logger.error);
      }
    },
    [isFactsDataOpen, metadata, structureModel, toggleFact]
  );

  const onToggle = useCallback(
    (id, header) => {
      const { isReaxysContent, isCIDatabase, isCommercialSupplierData } = database;
      const fact = get(facts, id);

      if (get(fact, 'hitData')) {
        toggleFact(fact.id, true, true);
        return;
      }

      if (isReaxysContent || isCIDatabase || isCommercialSupplierData) {
        toggleState('isFactsDataOpen', !fact?.open, fact, header);
        return;
      }

      onToggleAlternativeInfo(id);
    },
    [database, facts, toggleFact, toggleState, onToggleAlternativeInfo]
  );

  // autoOpenCategory logic

  useEffect(() => {
    const { isCommercialSupplierData } = database;

    if (Auth.isFeatureEnabled(FEATURES.enableDisplaySupplierNumber) && isCommercialSupplierData && substance.index === '1') {
      const proddFact = get(facts, 'PRODD');
      const hitDataFact = get(facts, 'HIT_DATA');
      if (proddFact && !hitDataFact) {
        onToggle('PRODD');
      }
      return;
    }
    if (metadata.heatmapMode && size === 1) {
      filter(facts, (fact) => fact.type === 'category').forEach((fact) => onToggle(fact.id));
    }

    const expandTopSubstanceFactDirectiveValue =
      substance.index === '1' &&
      metadata.uiDirectives &&
      getUIDirectiveValue(metadata.uiDirectives, UI_DIRECTIVES.EXPAND_TOP_SUBSTANCE_FACT);
    const expandTopSubstanceFactDirectiveAltValue = getExpandTopSubstanceFactMappingValue(expandTopSubstanceFactDirectiveValue);
    if (expandTopSubstanceFactDirectiveValue && some(facts, { id: expandTopSubstanceFactDirectiveValue })) {
      onToggle(expandTopSubstanceFactDirectiveValue);
    } else if (expandTopSubstanceFactDirectiveAltValue && some(facts, { id: expandTopSubstanceFactDirectiveAltValue })) {
      onToggle(expandTopSubstanceFactDirectiveAltValue);
    }
    // eslint-disable-next-line
  }, []);

  const toggleSuppliers = useCallback(() => onToggleAlternativeInfo('supplierInformation'), [onToggleAlternativeInfo]);
  const toggleCommercialSuppliers = useCallback(() => onToggleAlternativeInfo('commercialSupplierInformation'), [onToggleAlternativeInfo]);
  const toggleIdentification = useCallback(() => onToggleAlternativeInfo('identification'), [onToggleAlternativeInfo]);
  const toggleMoreNames = useCallback(() => onToggleAlternativeInfo('moreNames'), [onToggleAlternativeInfo]);

  const { identification, supplierInformation, moreNames } = alternativeContent;
  const actualZoom = metadata.isViewPreview ? 0 : zoom;
  const zoomClass = zoomClasses[actualZoom];
  const factsContextValue = useMemo(() => ({ facts, toggle: toggleFact }), [facts, toggleFact]);

  return (
    <div className="u-els-padding-top u-els-margin-top-1o2 u-els-margin-left substance-content">
      <FactsContext.Provider value={factsContextValue}>
        <div className="o-els-flex-layout o-els-flex-layout--left o-els-flex-layout--wrap@mobile">
          <div className="rx-reflow-scrollable">
            <div className={`substances-item-wrapper ${zoomClass}`}>
              <Structure metadata={metadata} zoom={actualZoom} structure={structureModel} isCard={gridView} />
            </div>
          </div>

          {!gridView && (
            <SubstanceDetailsContainer
              metadata={metadata}
              substance={structureModel}
              database={database}
              onToggle={onToggle}
              toggleSuppliers={toggleSuppliers}
              toggleCommercialSuppliers={toggleCommercialSuppliers}
              toggleIdentification={toggleIdentification}
              toggleMoreNames={toggleMoreNames}
            />
          )}
        </div>

        {!gridView && <SubstanceHitData metadata={metadata} substance={structureModel} database={database} />}

        {!gridView && isFactsDataOpen && (
          <SubstanceInformation
            metadata={metadata}
            facts={facts}
            toggleFact={toggleFact}
            substance={structureModel}
            database={database}
            onToggle={() => toggleState('isFactsDataOpen', false)}
            zoom={actualZoom}
          />
        )}

        {!gridView && identification && (
          <SubstanceIdentificationInformation substance={structureModel} database={database} onToggle={toggleIdentification} />
        )}

        {!gridView && supplierInformation && (
          <SubstanceSupplierInformation substance={structureModel} database={database} onToggle={toggleSuppliers} />
        )}

        {!gridView && moreNames && <SubstanceMoreNamesInformation substance={structureModel} onToggle={toggleMoreNames} />}
      </FactsContext.Provider>
    </div>
  );
}

Substance.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  zoom: PropTypes.number.isRequired,
  searchType: PropTypes.string,
  gridView: PropTypes.bool,
  size: PropTypes.number,
};

export default Substance;
